import type { EventBus, QualityTier, Settings } from '@/contracts';

const KEY = 'stg.settings.v1';

const DEFAULTS = {
  quality: 'high' as QualityTier,
  shadows: true,
  ssao: true,
  bloom: true,
  antialias: 'smaa' as Settings['antialias'],
  renderScale: 1,
  drawDistance: 900,
  vegetationDensity: 0.8,
  masterVolume: 0.9,
  sfxVolume: 1,
  musicVolume: 0.5,
  assists: true,
  invertLookY: false,
  colorblindSafe: false,
  uiScale: 1,
  reducedMotion: false,
};

/** Per-tier overrides applied when the player picks a quality preset. */
const TIERS: Record<QualityTier, Partial<typeof DEFAULTS>> = {
  low:    { shadows: false, ssao: false, bloom: false, antialias: 'off',  renderScale: 0.7, drawDistance: 400, vegetationDensity: 0.25 },
  medium: { shadows: true,  ssao: false, bloom: true,  antialias: 'smaa', renderScale: 0.85, drawDistance: 650, vegetationDensity: 0.5 },
  high:   { shadows: true,  ssao: true,  bloom: true,  antialias: 'smaa', renderScale: 1,   drawDistance: 900, vegetationDensity: 0.8 },
  ultra:  { shadows: true,  ssao: true,  bloom: true,  antialias: 'taa',  renderScale: 1,   drawDistance: 1400, vegetationDensity: 1 },
};

/**
 * Device-pixel-ratio ceiling per tier (docs/perf-plan.md item 1). Pixel count is
 * the single biggest lever on iGPU: a dpr-2 tablet at the default tier renders
 * 4x the pixels of a 1080p laptop through ~7 full-screen passes. Only ultra
 * keeps native retina; the capture harness runs at dpr 1, where this is a no-op.
 */
export const TIER_DPR: Record<QualityTier, number> = {
  low: 1.25, medium: 1.5, high: 1.5, ultra: 2,
};

/** Preset render scale for a tier — the governor's per-tier baseline. */
export function tierRenderScale(tier: QualityTier): number {
  return TIERS[tier].renderScale ?? 1;
}

export class SettingsStore implements Settings {
  quality = DEFAULTS.quality;
  shadows = DEFAULTS.shadows;
  ssao = DEFAULTS.ssao;
  bloom = DEFAULTS.bloom;
  antialias: 'off' | 'smaa' | 'taa' = DEFAULTS.antialias;
  renderScale = DEFAULTS.renderScale;
  drawDistance = DEFAULTS.drawDistance;
  vegetationDensity = DEFAULTS.vegetationDensity;
  masterVolume = DEFAULTS.masterVolume;
  sfxVolume = DEFAULTS.sfxVolume;
  musicVolume = DEFAULTS.musicVolume;
  assists = DEFAULTS.assists;
  invertLookY = DEFAULTS.invertLookY;
  colorblindSafe = DEFAULTS.colorblindSafe;
  uiScale = DEFAULTS.uiScale;
  reducedMotion = DEFAULTS.reducedMotion;

  constructor(private readonly events: EventBus) {
    this.load();
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) this.reducedMotion = true;
  }

  get<K extends keyof Settings>(k: K): Settings[K] {
    return this[k] as Settings[K];
  }

  set<K extends keyof Settings>(k: K, v: Settings[K]): void {
    if ((this as Settings)[k] === v) return;
    (this as Settings)[k] = v;
    if (k === 'quality') this.applyTier(v as QualityTier);
    this.save();
  }

  /** Applies a preset without clobbering the player's explicit audio/a11y choices. */
  applyTier(tier: QualityTier): void {
    Object.assign(this, TIERS[tier]);
    this.quality = tier;
    this.events.emit('quality:changed', { tier });
  }

  /**
   * Picks a starting tier from what the device actually is. Deliberately
   * conservative — it is far better to start smooth and let the player raise it.
   */
  autoDetectTier(): QualityTier {
    const mem = (navigator as { deviceMemory?: number }).deviceMemory ?? 4;
    const cores = navigator.hardwareConcurrency ?? 4;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    if (coarse || mem <= 2 || cores <= 2) return 'low';
    if (mem <= 4 || cores <= 4) return 'medium';
    if (mem >= 8 && cores >= 8) return 'high';
    return 'medium';
  }

  save(): void {
    try {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(DEFAULTS)) out[k] = (this as Record<string, unknown>)[k];
      localStorage.setItem(KEY, JSON.stringify(out));
    } catch {
      /* private browsing / quota — settings simply do not persist */
    }
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return;
      const saved = JSON.parse(raw) as Record<string, unknown>;
      for (const k of Object.keys(DEFAULTS)) {
        if (k in saved && typeof saved[k] === typeof (DEFAULTS as Record<string, unknown>)[k]) {
          (this as Record<string, unknown>)[k] = saved[k];
        }
      }
    } catch {
      /* corrupt payload — fall back to defaults rather than failing to boot */
    }
  }
}
